import { GlobalHomePage } from '@/features/shop/components/GlobalHomePage'
import { RouteName, routes } from '@shared/constants/routes'
import { createServerSidePropsHandler } from '@/features/shared/serverUtils/ssrHelpers/createServerSidePropsHandler'
import { getUrlWithNextParam } from '@/features/shared/utils/url'
import { userQueryOptions } from '@/features/account/services/User/queryOptions'
import { isCircle360UtmSource } from '@/features/entrypoint/components/Circle360/constants'
import {
  getPageRedirect,
  RedirectReason,
} from '@/features/shared/serverUtils/ssrHelpers/redirectReason'
import { applyGuestZipSSR } from '@/features/shared/serverUtils/storeResolutions/pagesAdapters'
import { isSoftNavigation } from '@/features/shared/serverUtils/ssrHelpers/isSoftNavigation'
import { getIsBot } from '@/features/shared/serverUtils/getIsBot'
import { prefetchCMSGlobalHeaderCategoriesForBotSSR } from '@/features/cms/services/CMS/serverUtils'
import { withGlobalHeaderLayout } from '@/features/shared/components/Headers/Layouts/GlobalHeaderLayout'
import { noop } from '@/features/shared/utils/noop'

type Props = { c360MembershipId?: string; checkC360Membership?: true }

export const getServerSideProps = createServerSidePropsHandler<Props>(
  RouteName.GLOBAL_HOMEPAGE,
  async ({
    ssrContext,
    queryClient,
    authContext: { isGuestUser, isAuthenticated, isOnboarded },
    contextualRedirect,
  }) => {
    // Handles redirecting to welcome flow for authenticated users who have not onboarded
    if (isAuthenticated && !isOnboarded) {
      return contextualRedirect || {}
    }

    const {
      shiptPasses,
      planId,
      student_email,
      redirect_from,
      utm_source,
      membership_id = '',
    } = ssrContext.query

    // isGuestUser means that they have a valid guest address stored in the cookie
    if (isGuestUser) {
      if (planId) {
        return getPageRedirect(
          getUrlWithNextParam(
            routes.SIGN_UP.url,
            `${routes.MEMBERSHIP_DASHBOARD.url}?planId=${planId}`,
          ),
          RedirectReason.GUEST_WITH_PLAN_ID,
        )
      }
      if (student_email) {
        return getPageRedirect(
          getUrlWithNextParam(
            routes.SIGN_UP.url,
            `${routes.MEMBERSHIP_DASHBOARD.url}?student_email=${student_email}`,
          ),
          RedirectReason.GUEST_WITH_STUDENT_EMAIL,
        )
      }
    }

    if (shiptPasses) {
      const finalRoute = routes.MEMBERSHIP_DASHBOARD.url
      const redirectReason = isGuestUser
        ? RedirectReason.GUEST_WITH_SHIPT_PASSES
        : RedirectReason.AUTHED_WITH_SHIPT_PASSES

      return getPageRedirect(
        isGuestUser ? getUrlWithNextParam(routes.SIGN_UP.url, finalRoute) : finalRoute,
        redirectReason,
      )
    }

    // If not logged in and no valid guest address cookie, try to set guest address zip
    if (!isGuestUser && !isAuthenticated) {
      const pageRedirect = await applyGuestZipSSR(ssrContext, queryClient)
      if (pageRedirect) return pageRedirect
    }

    // prefetch user data for authenticated users for hard navs. This helps LCP as it avoids
    // a request waterfall on the client
    if (isAuthenticated && !isSoftNavigation(ssrContext)) {
      await queryClient.query(userQueryOptions).catch(noop)
    }

    // Prefetch the CMS global-header-categories query for bots so that the
    // mobile navigation renders in the SSR HTML for crawlers. This runs after
    // `inferAndSetGuestZipOrRedirectHomeSSR` has set the guest address cookie
    // for bots, so the zip is available when building the query params.
    if (getIsBot(ssrContext.req.headers)) {
      await prefetchCMSGlobalHeaderCategoriesForBotSSR({
        ssrContext,
        queryClient,
      })
    }

    // modal will show if
    // 1. redirected from c360 page OR landed via utm source AND
    // 2. membership_id is present and membership_id does not match logged in user's platformCustomerId OR
    // 3. user is not c360 member
    let c360MembershipId: string | undefined
    let checkC360Membership: true | undefined
    if (redirect_from === RouteName.TARGET_CIRCLE_360 || isCircle360UtmSource(String(utm_source))) {
      c360MembershipId = membership_id ? decodeURIComponent(String(membership_id)) : undefined
      checkC360Membership = true
    }

    return {
      props: { c360MembershipId, checkC360Membership },
    }
  },
)

export default withGlobalHeaderLayout(GlobalHomePage, {
  showActivityPunchCardButton: true,
  isGlobalHomepage: true,
})
